import { CalendarDays, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RoomTab } from '../../utils/roomTabs';
import { LeaderboardFilters } from './LeaderboardFilters';

interface RoomTabsProps {
  tab: RoomTab;
  onChange: (tab: RoomTab) => void;
  bestCount?: number;
  showFilter?: boolean;
  minDuration?: number;
  onMinDurationChange?: (duration: number) => void;
}

export function RoomTabs({
  tab,
  onChange,
  bestCount = 0,
  showFilter = false,
  minDuration = 1,
  onMinDurationChange,
}: RoomTabsProps) {
  const { t } = useTranslation();

  const renderTab = (id: RoomTab, label: string, Icon: typeof CalendarDays, badge?: number) => {
    const active = tab === id;
    return (
      <button
        key={id}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => onChange(id)}
        className={`relative flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2.5 py-[11px] text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent ${
          active ? 'bg-room-surface-3 text-room-text' : 'bg-transparent text-room-text-dim'
        }`}
      >
        <Icon size={15} />
        <span>{label}</span>
        {badge && badge > 0 ? (
          <span
            className={`ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1.5 text-[11px] font-extrabold leading-none tabular-nums ${
              active ? 'bg-room-accent text-[#06231D]' : 'bg-room-surface-3 text-room-text-dim'
            }`}
          >
            {badge}
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <div className="relative mb-6 flex w-full items-center gap-1 rounded-2xl border border-room-border bg-room-surface p-1">
      <div role="tablist" className="flex min-w-0 flex-1 gap-1">
        {renderTab('mark', t('roomTabs.mark', { defaultValue: 'Mark times' }), CalendarDays)}
        {renderTab('best', t('roomTabs.best', { defaultValue: 'Best slots' }), Trophy, bestCount)}
      </div>
      {showFilter && onMinDurationChange ? (
        <LeaderboardFilters minDuration={minDuration} onMinDurationChange={onMinDurationChange} />
      ) : null}
    </div>
  );
}
